/**
 * useAiSearchStream
 *
 * Two-step streaming hook for Phase 2 unified search:
 *   1. Call `aiSearch` (query) to get curated content, playlists, profiles,
 *      chatSessionId, intentSignal, entities, and adjacency.
 *   2. Subscribe to `oracleSearchOverviewStream` with the session context
 *      to stream the Sage narrative overlay.
 *
 * Supports search, refine (re-query with sessionId), loadMore (offset
 * pagination for content), and sendFeedback.
 *
 * State machine: IDLE -> LOADING -> STREAMING -> RESULTS | ERROR
 */

import {
  useState, useCallback, useRef, useEffect,
} from 'react'
import { gql, useApolloClient } from '@apollo/client'
import type { Observable, FetchResult } from '@apollo/client'
import {
  APOLLO_CLIENT_FETCH_POLICY_NO_CACHE,
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
} from '../../apollo-graphql/index'
import { addNewRelicAction } from '../../app/_newrelic'
import type {
  OracleContentResult,
  OracleContentEntity,
  AiPlaylistTileResult,
  OracleProfileResult,
} from './useOracleSearch'
import { normalizeBracketTags } from './useOracleSearchStream'
import {
  AI_SEARCH_CONTENT_QUERY,
  AI_SEARCH_PLAYLISTS_QUERY,
  AI_SEARCH_PROFILES_QUERY,
  OVERVIEW_STREAM_SUBSCRIPTION,
  SEARCH_TILE_HYDRATION_QUERY,
} from './ai-search-queries'

// ── GraphQL documents ───────────────────────────────────────────────────

const ORACLE_SEARCH_FEEDBACK_MUTATION = gql`
  mutation OracleSearchFeedback($input: OracleSearchFeedbackInput!) {
    oracleSearchFeedback(input: $input) {
      success
      errors
      thumbsValue
    }
  }
`

// ── Types ────────────────────────────────────────────────────────────

export type AiSearchStreamState = 'IDLE' | 'LOADING' | 'STREAMING' | 'RESULTS' | 'ERROR'

interface AiSearchContentResult {
  contentId: number | string
  contentType: string
  title: string
  score: number
  channel: string
  personalizationBoost: number
  publishedAtDate: string | null
  sortPopular: number | null
}

interface AiSearchPlaylistResult {
  playlistId: string
  playlistName: string
  description: string | null
  itemCount: number | null
  matchedContentCount: number
  textRelevance: number
  score: number
  previewThumbnailUrls: string[]
  previewContentTitles: string[]
  creatorDisplayName: string | null
  playlist?: {
    id: string
    visibility: 'PUBLIC' | 'ANONYMOUS' | 'PRIVATE'
    followerCount?: number
    isFollowing?: boolean
    userProfile?: {
      uuid?: string
      displayName?: string
      avatar?: { url?: string; small?: string; card?: string } | null
    } | null
    playlistItems?: Array<{
      id: string
      contentId: number | string
      contentType: string
      playlistPosition?: number
      content?: unknown[] | null
    }> | null
  } | null
}

interface AiSearchProfileResult {
  uid: string
  username: string
  displayName?: string | null
  bio: string | null
  score: number
  userProfile: {
    uid: string
    bio: string | null
    followerCount: number
    profileVisibility?: string | null
    viewerContext?: {
      isFollowing?: boolean | null
      isFollowedBy?: boolean | null
    } | null
    uuid?: string | null
    displayName?: string | null
    avatar?: { url?: string; small?: string; card?: string } | null
    topics?: Array<{ contentId: number | string; title: string }> | null
    topGenres?: Array<{ name: string; cleanName: string }> | null
  } | null
}

interface IntentSignal {
  classification: string
  reason: string
  requiresLlm: boolean
}

interface EntityItem {
  contentId: number | null
  contentType: string
  entityType: string
  name: string
  titleGroupId: string
  confidence: number
  matchType: string
}

interface AdjacencyItem {
  centerId: number
  centerName: string
  sharedContentCount: number
  adjacencyType: string
}

// ── Split query response types ──────────────────────────────────────

interface AiSearchContentData {
  chatSessionId: string | null
  intentSignal: IntentSignal | null
  entities: EntityItem[] | null
  adjacency: AdjacencyItem[] | null
  content: AiSearchContentResult[] | null
  contentTotalCount: number
  topContentIds: number[]
  topContentScores: number[]
  blacklistMessage: string | null
}

interface AiSearchPlaylistsData {
  playlists: AiSearchPlaylistResult[] | null
  playlistTotalCount: number
}

type AiSearchProfileData = AiSearchProfileResult

// ── Overview stream payload ─────────────────────────────────────────

interface OverviewStreamPayload {
  oracleSearchOverviewStream: {
    chatSessionId: string | null
    turnId: string | null
    textChunk: string | null
    statusText: string | null
    refinementSuggestions: string[] | null
    executionTime: number | null
    isFinal: boolean
    success: boolean
    errors: string[] | null
  }
}

export interface AiSearchResults {
  content: OracleContentResult[]
  playlists: AiPlaylistTileResult[]
  profiles: OracleProfileResult[]
}

const EMPTY_RESULTS: AiSearchResults = {
  content: [],
  playlists: [],
  profiles: [],
}

const AI_SEARCH_LIMIT = 16
// Profile result cap. The backend defaults `profileLimit` to 5 and does
// NOT yet expose a `profileOffset`, so we can't paginate. Bumping the
// initial cap returns more profiles in a single round-trip.
// `retrieveProfileResults` (gaia-brooklyn-api/lib/services/ai-search/
// retrieval.ts:830) enforces no server-side ceiling — it runs Postgres
// FTS + trigram name search on `limit * 2` candidates each, then
// `slice(0, limit)`. So the chosen value is purely a frontend UX/perf
// trade-off: 30 is roughly 5–7 grid rows in S12345, enough to feel
// "all results" for typical name-search queries without ballooning the
// DB-side candidate pool. When the backend grows `profileOffset`,
// switch this surface to real pagination.
const AI_SEARCH_PROFILE_LIMIT = 30

export interface SearchFilters {
  level?: string | null
  duration?: string | null
  refinementType?: string | null
  entityId?: number | null
  seriesContentId?: number | null
}

export type SearchSort = 'RELEVANCE' | 'DATE_ADDED' | 'POPULARITY' | 'A_Z'

/** Per-call overrides for content/profile limits (used by Discover tab for playlist-only mode). */
export interface AiSearchLimitOverrides {
  contentLimit?: number
  profileLimit?: number
}

export interface UseAiSearchStreamReturn {
  search: (query: string | null, overrides?: AiSearchLimitOverrides, skipOverview?: boolean) => void
  changeSort: (sort: SearchSort) => void
  refine: (text: string, query?: string, filters?: SearchFilters | null) => void
  reset: () => void
  loadMore: () => void
  sendFeedback: (thumbsValue: 'up' | 'down' | 'none') => Promise<void>
  state: AiSearchStreamState
  statusKey: string | null
  streamingText: string
  results: AiSearchResults
  refinementSuggestions: string[]
  error: string | null
  blacklistMessage: string | null
  chatSessionId: string | null
  turnId: string | null
  contentTotalCount: number
  playlistTotalCount: number
  profileTotalCount: number
  activeQuery: string | null
}

// ── Result mapping helpers ──────────────────────────────────────────

function mapContentResults (raw: AiSearchContentResult[]): OracleContentResult[] {
  return raw
    .filter((r) => r.contentId)
    .map((r) => ({
      contentId: r.contentId,
      brooklynId: '',
      contentType: r.contentType,
      title: r.title,
      score: r.score || 0,
      content: null,
    }))
}

function mapPlaylistResults (raw: AiSearchPlaylistResult[]): AiPlaylistTileResult[] {
  return raw.map((r) => ({
    playlistId: r.playlistId,
    playlistName: r.playlistName || '',
    description: r.description ?? null,
    itemCount: r.itemCount ?? null,
    score: r.score || 0,
    previewThumbnailUrls: r.previewThumbnailUrls || [],
    previewContentTitles: r.previewContentTitles || [],
    creatorDisplayName: r.creatorDisplayName ?? null,
    playlist: r.playlist || null,
  }))
}

function mapProfileResults (raw: AiSearchProfileResult[]): OracleProfileResult[] {
  return raw.map((r) => ({
    uid: r.uid,
    username: r.username,
    bio: r.bio,
    interests: null,
    score: r.score || 0,
    profile: r.userProfile ? {
      uid: r.userProfile.uid,
      bio: r.userProfile.bio,
      followerCount: r.userProfile.followerCount,
      profileVisibility: r.userProfile.profileVisibility ?? null,
      viewerContext: r.userProfile.viewerContext ? {
        isFollowing: r.userProfile.viewerContext.isFollowing ?? false,
        isFollowedBy: r.userProfile.viewerContext.isFollowedBy ?? false,
      } : null,
      userInfo: r.userProfile.uuid ? {
        uuid: r.userProfile.uuid,
        username: r.userProfile.displayName || '',
        displayName: r.userProfile.displayName,
        profilePicture: r.userProfile.avatar ? {
          hdtv_190x266: r.userProfile.avatar.card
            || r.userProfile.avatar.url
            || null,
          small_28x28: r.userProfile.avatar.small || null,
        } : null,
      } : null,
      topics: r.userProfile.topics ? r.userProfile.topics.map((t) => ({
        id: t.contentId,
        label: t.title,
        category: '',
      })) : null,
      topGenres: r.userProfile.topGenres || null,
    } : null,
  }))
}

const SEARCH_IMAGE_TYPES = ['d6-keyart-with-text', 'd6-keyart-no-text', 'd6-poster-image']

/**
 * Hydrate search results with tile data from contentList (cache-first).
 * Content entities already in Apollo Client cache are served instantly.
 */
async function hydrateContentTiles (
  client: ReturnType<typeof useApolloClient>,
  results: OracleContentResult[],
  language: string,
  jwt: string | null,
): Promise<OracleContentResult[]> {
  const pending = results.filter((r) => !r.content && r.contentId)
  if (pending.length === 0) return results

  const titleGroupIds = pending.map((r) => `${r.contentId}-${r.contentType}`)

  try {
    const { data } = await client.query<{
      contentList: { content: OracleContentEntity[] }
    }>({
      query: SEARCH_TILE_HYDRATION_QUERY,
      variables: {
        titleGroupIds,
        language: [language, 'en', '_primary'],
        imageType: SEARCH_IMAGE_TYPES,
      },
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
      context: { jwt },
    })

    const entityMap = new Map<number, OracleContentEntity>()
    const contentItems = data?.contentList?.content || []
    contentItems.forEach((c) => {
      if (c.contentId) entityMap.set(Number(c.contentId), c)
    })

    return results.map((r) => {
      const entity = entityMap.get(Number(r.contentId))
      return entity ? { ...r, content: entity } : r
    })
  } catch {
    // Hydration failure is non-fatal — tiles show as skeletons
    return results
  }
}

// ── Hook ─────────────────────────────────────────────────────────────

export default function useAiSearchStream (
  jwt: string | null,
  language: string,
  options?: { enablePlaylistsAndProfiles?: boolean },
): UseAiSearchStreamReturn {
  const enablePlaylistsAndProfiles = options?.enablePlaylistsAndProfiles ?? true
  const client = useApolloClient()

  const [state, setState] = useState<AiSearchStreamState>('IDLE')
  const [statusKey, setStatusKey] = useState<string | null>(null)
  const [streamingText, setStreamingText] = useState('')
  const [results, setResults] = useState<AiSearchResults>(EMPTY_RESULTS)
  const [refinementSuggestions, setRefinementSuggestions] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [chatSessionId, setChatSessionId] = useState<string | null>(null)
  const [turnId, setTurnId] = useState<string | null>(null)
  const [contentTotalCount, setContentTotalCount] = useState(0)
  const [playlistTotalCount, setPlaylistTotalCount] = useState(0)
  const [profileTotalCount, setProfileTotalCount] = useState(0)
  const [blacklistMessage, setBlacklistMessage] = useState<string | null>(null)

  // Stable refs
  const jwtRef = useRef(jwt)
  const languageRef = useRef(language)
  jwtRef.current = jwt
  languageRef.current = language

  const stateRef = useRef<AiSearchStreamState>(state)
  stateRef.current = state

  type Subscription = { unsubscribe: () => void }
  const subscriptionRef = useRef<Subscription | null>(null)
  const requestIdRef = useRef(0)
  // Dedicated counter for content pagination. loadMore must NOT bump
  // requestIdRef — that ref gates the open overview subscription, so
  // invalidating it mid-stream wedges the state machine at 'STREAMING'.
  const loadMoreIdRef = useRef(0)
  const searchTimingRef = useRef<{ t0: number, t2: number | null, firstChunkRecorded: boolean }>({
    t0: 0, t2: null, firstChunkRecorded: false,
  })
  const contentOffsetRef = useRef(0)
  const sessionIdRef = useRef<string | null>(null)
  const lastQueryRef = useRef<string | null>('')
  const limitOverridesRef = useRef<AiSearchLimitOverrides | undefined>(undefined)
  const intentRef = useRef<IntentSignal | null>(null)
  const entitiesRef = useRef<EntityItem[] | null>(null)
  const adjacencyRef = useRef<AdjacencyItem[] | null>(null)
  const sortRef = useRef<SearchSort | undefined>(undefined)
  type SortCacheEntry = {
    content: OracleContentResult[]
    totalCount: number
    offset: number
  }
  const sortCacheRef = useRef<Map<string, SortCacheEntry>>(new Map())

  // Coalesce streaming-text updates to one render per animation frame.
  // Many overview chunks per second otherwise cascade re-renders through
  // every DiscoverPlaylistRow consumer of `useDiscoverTabState`.
  const pendingStreamingTextRef = useRef<string | null>(null)
  const streamingTextRafRef = useRef<number | null>(null)

  const flushPendingStreamingText = useCallback(() => {
    streamingTextRafRef.current = null
    if (pendingStreamingTextRef.current === null) return
    setStreamingText(pendingStreamingTextRef.current)
    pendingStreamingTextRef.current = null
  }, [])

  /**
   * Subscribes to the overview stream after aiSearch returns.
   */
  const subscribeOverview = useCallback((
    sessionId: string,
    query: string,
    currentRequestId: number,
  ) => {
    // Teardown previous subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
      subscriptionRef.current = null
    }

    // Brooklyn already seeded full context (entities, adjacency, content, playlists)
    // to AI Service via POST /api/search-overview/context. The subscription only
    // needs chatSessionId to load it from the DB. No need to round-trip data
    // through the frontend.
    const input: Record<string, unknown> = {
      chatSessionId: sessionId,
      query: query || '',
      language: languageRef.current,
    }

    let accumulated = ''
    const observable: Observable<FetchResult<OverviewStreamPayload>> = client.subscribe({
      query: OVERVIEW_STREAM_SUBSCRIPTION,
      variables: { input },
      context: { jwt: jwtRef.current },
    })

    const subscription = observable.subscribe({
      next: (response) => {
        if (requestIdRef.current !== currentRequestId) return

        const payload = response.data?.oracleSearchOverviewStream
        if (!payload) return

        if (payload.statusText) {
          setStatusKey(payload.statusText)
        }

        if (payload.textChunk && !payload.isFinal) {
          setStatusKey(null)
          accumulated += payload.textChunk
          const normalized = normalizeBracketTags(accumulated)
          if (normalized.length > 0) {
            pendingStreamingTextRef.current = normalized
            if (streamingTextRafRef.current === null) {
              streamingTextRafRef.current = requestAnimationFrame(flushPendingStreamingText)
            }
          }
          if (stateRef.current !== 'STREAMING') {
            setState('STREAMING')
          }

          // T4: first text chunk — record once per search
          const timing = searchTimingRef.current
          if (!timing.firstChunkRecorded && timing.t0 > 0) {
            timing.firstChunkRecorded = true
            const now = performance.now()
            addNewRelicAction('AiSearchOverviewFirstChunk', {
              firstChunkMs: Math.round(now - timing.t0),
              overviewLatencyMs: timing.t2 ? Math.round(now - timing.t2) : null,
              query: query || '',
            })
          }
        }

        if (payload.chatSessionId) {
          setChatSessionId(payload.chatSessionId)
          sessionIdRef.current = payload.chatSessionId
        }
        if (payload.turnId) {
          setTurnId(payload.turnId)
        }

        if (payload.isFinal) {
          // T5: stream complete
          const timing = searchTimingRef.current
          if (timing.t0 > 0) {
            const now = performance.now()
            addNewRelicAction('AiSearchOverviewComplete', {
              totalMs: Math.round(now - timing.t0),
              streamDurationMs: timing.t2 ? Math.round(now - timing.t2) : null,
              success: !!payload.success,
              query: query || '',
              executionTime: payload.executionTime ?? null,
            })
          }

          if (!payload.success) {
            // Overview failed but we still have results — just move to RESULTS
            // instead of ERROR so tiles remain visible.
            setState('RESULTS')
            return
          }
          if (payload.textChunk) {
            // Final paint must be exact — bypass the rAF throttle.
            if (streamingTextRafRef.current !== null) {
              cancelAnimationFrame(streamingTextRafRef.current)
              streamingTextRafRef.current = null
            }
            pendingStreamingTextRef.current = null
            setStreamingText(normalizeBracketTags(payload.textChunk))
          }
          if (payload.refinementSuggestions && payload.refinementSuggestions.length > 0) {
            setRefinementSuggestions(payload.refinementSuggestions)
          }
          setState('RESULTS')
        }
      },
      error: () => {
        if (requestIdRef.current !== currentRequestId) return
        // Overview stream error is non-fatal — tiles are already shown
        setState('RESULTS')
      },
    })

    subscriptionRef.current = subscription
  }, [client, flushPendingStreamingText])

  /**
   * Executes an aiSearch query, stores results, then subscribes to overview.
   */
  const executeSearch = useCallback(async (
    query: string | null,
    sessionId?: string | null,
    offset = 0,
    filters?: SearchFilters | null,
    overrides?: AiSearchLimitOverrides,
    sort?: SearchSort,
    skipOverview?: boolean,
  ) => {
    lastQueryRef.current = query

    // Cancel any pending streaming-text frame so a brand-new search
    // doesn't paint a stale tail chunk on the next animation frame.
    if (streamingTextRafRef.current !== null) {
      cancelAnimationFrame(streamingTextRafRef.current)
      streamingTextRafRef.current = null
    }
    pendingStreamingTextRef.current = null

    // Teardown previous subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
      subscriptionRef.current = null
    }

    requestIdRef.current += 1
    const currentRequestId = requestIdRef.current

    // T0: search initiated — start timing
    searchTimingRef.current = { t0: performance.now(), t2: null, firstChunkRecorded: false }

    setState('LOADING')
    setStatusKey('searching')
    setStreamingText('')
    setError(null)
    setRefinementSuggestions([])
    setTurnId(null)

    if (offset === 0) {
      setResults(EMPTY_RESULTS)
      contentOffsetRef.current = 0
    }

    const vars: Record<string, unknown> = {
      query: query || null,
      contentLimit: overrides?.contentLimit ?? AI_SEARCH_LIMIT,
      contentOffset: offset,
      profileLimit: overrides?.profileLimit ?? AI_SEARCH_PROFILE_LIMIT,
      language: [languageRef.current, 'en', '_primary'],
      learnerLevelLanguage: languageRef.current,
    }
    if (sessionId) {
      vars.sessionId = sessionId
    }
    if (filters) {
      vars.filters = filters
    }

    try {
      // T0: Fire content + profiles in parallel
      const contentVars: Record<string, unknown> = {
        query: query || null,
        contentLimit: overrides?.contentLimit ?? AI_SEARCH_LIMIT,
        contentOffset: offset,
      }
      if (sessionId) contentVars.sessionId = sessionId
      if (filters) contentVars.filters = filters
      if (sort) contentVars.sort = sort
      if (skipOverview) contentVars.skipOverview = true

      const profileVars: Record<string, unknown> = {
        query: query || '',
        profileLimit: overrides?.profileLimit ?? AI_SEARCH_PROFILE_LIMIT,
      }

      const [contentResult, profilesResult] = await Promise.all([
        client.query<{ search: AiSearchContentData }>({
          query: AI_SEARCH_CONTENT_QUERY,
          variables: contentVars,
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
          context: { jwt: jwtRef.current },
        }),
        query && enablePlaylistsAndProfiles && (profileVars.profileLimit as number) !== 0
          ? client.query<{ aiSearchProfiles: AiSearchProfileData[] }>({
            query: AI_SEARCH_PROFILES_QUERY,
            variables: profileVars,
            fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NO_CACHE,
            context: { jwt: jwtRef.current },
          }).catch(() => null)
          : Promise.resolve(null),
      ])

      if (requestIdRef.current !== currentRequestId) return

      const contentData = contentResult.data?.search
      if (!contentData) {
        setState('ERROR')
        setError('Search failed')
        return
      }

      // T2: content tiles received — render immediately
      const t2 = performance.now()
      searchTimingRef.current.t2 = t2
      const tilesTotalMs = Math.round(t2 - searchTimingRef.current.t0)

      addNewRelicAction('AiSearchTiles', {
        tilesTotalMs,
        query: query || '',
        contentCount: (contentData.content || []).length,
        contentTotalCount: contentData.contentTotalCount || 0,
        intent: contentData.intentSignal?.classification || '',
        hasSessionId: !!contentData.chatSessionId,
      })

      // Store session context for overview + refinements
      const newSessionId = contentData.chatSessionId || sessionId || null
      setChatSessionId(newSessionId)
      sessionIdRef.current = newSessionId
      intentRef.current = contentData.intentSignal || null
      entitiesRef.current = contentData.entities || null
      adjacencyRef.current = contentData.adjacency || null

      setBlacklistMessage(contentData.blacklistMessage || null)

      // Phase 1: Set lightweight results immediately (skeletons render for unhyrated tiles)
      const content = mapContentResults(contentData.content || [])
      setResults((prev) => ({ ...prev, content }))
      setContentTotalCount(contentData.contentTotalCount || content.length)
      contentOffsetRef.current = offset + content.length

      // Phase 2: Hydrate tiles from contentList (cache-first — instant for cached content)
      hydrateContentTiles(client, content, languageRef.current, jwtRef.current).then((hydrated) => {
        if (requestIdRef.current !== currentRequestId) return
        setResults((prev) => ({ ...prev, content: hydrated }))
        // Cache hydrated RELEVANCE results so sort switching back is instant
        if (offset === 0 && !sort) {
          sortCacheRef.current.set('RELEVANCE', { content: hydrated, totalCount: contentData.contentTotalCount || hydrated.length, offset: hydrated.length })
        }
      })

      // Profiles — may have already resolved or failed
      if (profilesResult?.data?.aiSearchProfiles) {
        const profiles = mapProfileResults(profilesResult.data.aiSearchProfiles)
        setResults((prev) => ({ ...prev, profiles }))
        setProfileTotalCount(profiles.length)
      }

      // Subscribe to overview stream (has chatSessionId now)
      // Skip when skipOverview is true (sort changes, non-flag users)
      if (skipOverview || !newSessionId) {
        setState('RESULTS')
      } else {
        subscribeOverview(newSessionId, query, currentRequestId)
      }

      // Playlist-only mode (Discover tab, contentLimit:0) fires even with an
      // empty query — the resolver personalizes from the JWT user context.
      const isPlaylistOnlyMode = overrides?.contentLimit === 0
      const shouldFirePlaylists = enablePlaylistsAndProfiles
        && (isPlaylistOnlyMode || (query && (contentData.topContentIds?.length || 0) > 0))
      if (shouldFirePlaylists) {
        client.query<{ aiSearchPlaylists: AiSearchPlaylistsData }>({
          query: AI_SEARCH_PLAYLISTS_QUERY,
          variables: {
            query: query || '',
            topContentIds: contentData.topContentIds || [],
            topContentScores: contentData.topContentScores || [],
            playlistLimit: 15,
            language: [languageRef.current, 'en', '_primary'],
          },
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NO_CACHE,
          context: { jwt: jwtRef.current },
        }).then((playlistResult) => {
          if (requestIdRef.current !== currentRequestId) return
          const playlistData = playlistResult.data?.aiSearchPlaylists
          if (playlistData) {
            const playlists = mapPlaylistResults(playlistData.playlists || [])
            setResults((prev) => ({ ...prev, playlists }))
            setPlaylistTotalCount(playlistData.playlistTotalCount || playlists.length)
            addNewRelicAction('AiSearchPlaylistsLoaded', {
              playlistsMs: Math.round(performance.now() - searchTimingRef.current.t0),
              playlistCount: playlists.length,
              query: query || '',
            })
          }
        }).catch(() => {
          // Playlist failure is non-fatal — content tiles already shown
        })
      }
    } catch (e) {
      if (requestIdRef.current !== currentRequestId) return
      const msg = e instanceof Error ? e.message : 'Search failed'
      setState('ERROR')
      setStatusKey(null)
      setError(msg)
    }
  }, [client, subscribeOverview])

  const search = useCallback((
    query: string | null,
    overrides?: AiSearchLimitOverrides,
    skipOverview?: boolean,
  ) => {
    sessionIdRef.current = null
    sortRef.current = undefined
    sortCacheRef.current.clear()
    setChatSessionId(null)
    setTurnId(null)
    limitOverridesRef.current = overrides
    executeSearch(query, null, 0, null, overrides, undefined, skipOverview)
  }, [executeSearch])

  const changeSort = useCallback((sort: SearchSort) => {
    sortRef.current = sort

    // Cache hit — instant swap, no network call
    const cached = sortCacheRef.current.get(sort)
    if (cached) {
      setResults((prev) => ({ ...prev, content: cached.content }))
      setContentTotalCount(cached.totalCount)
      contentOffsetRef.current = cached.offset
      return
    }

    // Cache miss — clear results so skeleton loading state shows
    setState('LOADING')
    setResults((prev) => ({ ...prev, content: [] }))
    contentOffsetRef.current = 0

    const vars: Record<string, unknown> = {
      query: lastQueryRef.current || null,
      contentLimit: limitOverridesRef.current?.contentLimit ?? AI_SEARCH_LIMIT,
      contentOffset: 0,
      sort,
      skipOverview: true,
    }
    if (sessionIdRef.current) {
      vars.sessionId = sessionIdRef.current
    }

    client.query<{ search: AiSearchContentData }>({
      query: AI_SEARCH_CONTENT_QUERY,
      variables: vars,
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
      context: { jwt: jwtRef.current },
    }).then(async (result) => {
      if (sortRef.current !== sort) return // user switched away
      const data = result.data?.search
      if (!data) return
      const content = mapContentResults(data.content || [])
      setResults((prev) => ({ ...prev, content }))
      setContentTotalCount(data.contentTotalCount || content.length)
      // Hydrate tiles, then cache the hydrated results
      const lang = languageRef.current
      const authToken = jwtRef.current
      const hydrated = await hydrateContentTiles(client, content, lang, authToken)
      if (sortRef.current !== sort) return
      const totalCount = data.contentTotalCount || hydrated.length
      sortCacheRef.current.set(sort, { content: hydrated, totalCount, offset: hydrated.length })
      setResults((prev) => ({ ...prev, content: hydrated }))
      contentOffsetRef.current = content.length
      setState('IDLE')
    }).catch(() => {
      // Sort failure is non-fatal — keep showing current results
      setState('IDLE')
    })
  }, [client])

  const refine = useCallback((text: string, query?: string, filters?: SearchFilters | null) => {
    sortCacheRef.current.clear()
    executeSearch(query || text, sessionIdRef.current, 0, filters, limitOverridesRef.current)
  }, [executeSearch])

  const loadMore = useCallback(() => {
    if (state === 'LOADING') return
    // loadMore uses content-only query — no playlists/profiles/overview.
    // Use a dedicated counter so paginating does NOT invalidate an open
    // overview subscription (which is gated by requestIdRef).
    const lmId = loadMoreIdRef.current + 1
    loadMoreIdRef.current = lmId
    const searchIdAtCall = requestIdRef.current
    const offset = contentOffsetRef.current

    const lmOverrides = limitOverridesRef.current
    const vars: Record<string, unknown> = {
      query: lastQueryRef.current || null,
      contentLimit: lmOverrides?.contentLimit ?? AI_SEARCH_LIMIT,
      contentOffset: offset,
      skipOverview: true,
    }
    if (sessionIdRef.current) {
      vars.sessionId = sessionIdRef.current
    }
    if (sortRef.current) {
      vars.sort = sortRef.current
    }

    client.query<{ search: AiSearchContentData }>({
      query: AI_SEARCH_CONTENT_QUERY,
      variables: vars,
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
      context: { jwt: jwtRef.current },
    }).then(async (result) => {
      // Bail if a newer loadMore superseded this one, OR a new search /
      // reset (which bumps requestIdRef) made these results stale.
      if (loadMoreIdRef.current !== lmId) return
      if (requestIdRef.current !== searchIdAtCall) return
      const data = result.data?.search
      if (!data) return

      const newContent = mapContentResults(data.content || [])
      const lang = languageRef.current
      const authToken = jwtRef.current
      const hydrated = await hydrateContentTiles(client, newContent, lang, authToken)
      if (loadMoreIdRef.current !== lmId) return
      if (requestIdRef.current !== searchIdAtCall) return

      contentOffsetRef.current = offset + hydrated.length
      const totalCount = data.contentTotalCount || (offset + hydrated.length)

      setResults((prev) => {
        const merged = [...prev.content, ...hydrated]
        const sortKey = sortRef.current || 'RELEVANCE'
        sortCacheRef.current.set(sortKey, {
          content: merged,
          totalCount,
          offset: offset + hydrated.length,
        })
        return { ...prev, content: merged }
      })
      setContentTotalCount(totalCount)
    }).catch(() => {
      // loadMore failure is non-fatal
    })
  }, [client, state])

  const reset = useCallback(() => {
    if (streamingTextRafRef.current !== null) {
      cancelAnimationFrame(streamingTextRafRef.current)
      streamingTextRafRef.current = null
    }
    pendingStreamingTextRef.current = null
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
      subscriptionRef.current = null
    }
    requestIdRef.current += 1
    setState('IDLE')
    setStatusKey(null)
    setStreamingText('')
    setResults(EMPTY_RESULTS)
    setRefinementSuggestions([])
    setError(null)
    setBlacklistMessage(null)
    setChatSessionId(null)
    setTurnId(null)
    setContentTotalCount(0)
    setPlaylistTotalCount(0)
    setProfileTotalCount(0)
    contentOffsetRef.current = 0
    sessionIdRef.current = null
    sortRef.current = undefined
    sortCacheRef.current.clear()
    intentRef.current = null
    entitiesRef.current = null
    adjacencyRef.current = null
  }, [])

  const sendFeedback = useCallback(async (thumbsValue: 'up' | 'down' | 'none') => {
    if (!turnId) return
    try {
      await client.mutate<{
        oracleSearchFeedback: {
          success: boolean
          errors: string[] | null
          thumbsValue: string
        }
      }>({
        mutation: ORACLE_SEARCH_FEEDBACK_MUTATION,
        variables: { input: { turnId, thumbsValue } },
        context: { jwt: jwtRef.current },
      })
    } catch {
      // non-fatal: feedback is fire-and-forget
    }
  }, [turnId, client])

  // Cleanup subscription on unmount
  useEffect(() => {
    return () => {
      requestIdRef.current += 1
      if (streamingTextRafRef.current !== null) {
        cancelAnimationFrame(streamingTextRafRef.current)
        streamingTextRafRef.current = null
      }
      pendingStreamingTextRef.current = null
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
        subscriptionRef.current = null
      }
    }
  }, [])

  return {
    search,
    changeSort,
    refine,
    reset,
    loadMore,
    sendFeedback,
    state,
    statusKey,
    streamingText,
    results,
    refinementSuggestions,
    error,
    blacklistMessage,
    chatSessionId,
    turnId,
    contentTotalCount,
    playlistTotalCount,
    profileTotalCount,
    activeQuery: lastQueryRef.current,
  }
}
